import React from 'react';
import { Check, XCircle, Clock, Eye, CheckCircle2, Loader2 } from 'lucide-react';
import type { QualityRating, DocumentStatus, FindingSeverity, FeedbackStatus } from '../../data/mock';

// ── Quality Badge ──────────────────────────────────────────────
const qualityConfig: Record<QualityRating, { className: string; label: string }> = {
  satisfactory: { className: 'badge-success', label: 'Satisfactory' },
  needs_improvement: { className: 'badge-warning', label: 'Needs Improvement' },
  unsatisfactory: { className: 'badge-danger', label: 'Unsatisfactory' },
};

interface QualityBadgeProps {
  quality: QualityRating;
}

export const QualityBadge: React.FC<QualityBadgeProps> = ({ quality }) => {
  const config = qualityConfig[quality];
  if (!config) return null;
  return (
    <span className={config.className}>
      {config.label}
    </span>
  );
};

// ── Document Status Badge ──────────────────────────────────────
const documentStatusConfig: Record<DocumentStatus, { className: string; label: string; icon: React.ReactNode }> = {
  processing: {
    className: 'badge-info',
    label: 'Processing',
    icon: <Loader2 size={12} className="animate-spin" />,
  },
  completed: {
    className: 'badge-success',
    label: 'Completed',
    icon: <CheckCircle2 size={12} />,
  },
  failed: {
    className: 'badge-danger',
    label: 'Failed',
    icon: <XCircle size={12} />,
  },
};

interface DocumentStatusBadgeProps {
  status: DocumentStatus;
}

export const DocumentStatusBadge: React.FC<DocumentStatusBadgeProps> = ({ status }) => {
  const config = documentStatusConfig[status];
  if (!config) return null;
  return (
    <span className={`${config.className} inline-flex items-center gap-1`}>
      {config.icon}
      {config.label}
    </span>
  );
};

// ── Severity Badge ─────────────────────────────────────────────
const severityConfig: Record<FindingSeverity, { className: string }> = {
  critical: { className: 'badge-danger' },
  high: { className: 'bg-orange-50 text-orange-700 px-2.5 py-0.5 rounded-full text-[11px] font-medium' },
  medium: { className: 'badge-warning' },
  low: { className: 'bg-surface-100 text-surface-600 px-2.5 py-0.5 rounded-full text-[11px] font-medium' },
};

interface SeverityBadgeProps {
  severity: FindingSeverity;
}

export const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
  const config = severityConfig[severity];
  return (
    <span className={`${config.className} capitalize`}>
      {severity}
    </span>
  );
};

// ── Feedback Status Badge ──────────────────────────────────────
const feedbackStatusConfig: Record<FeedbackStatus, { className: string; label: string; icon: React.ReactNode }> = {
  open: {
    className: 'badge-info',
    label: 'Open',
    icon: <Clock size={12} />,
  },
  in_review: {
    className: 'badge-warning',
    label: 'In Review',
    icon: <Eye size={12} />,
  },
  resolved: {
    className: 'badge-success',
    label: 'Resolved',
    icon: <Check size={12} />,
  },
};

interface FeedbackStatusBadgeProps {
  status: FeedbackStatus;
}

export const FeedbackStatusBadge: React.FC<FeedbackStatusBadgeProps> = ({ status }) => {
  const config = feedbackStatusConfig[status];
  if (!config) return null;
  return (
    <span className={`${config.className} inline-flex items-center gap-1`}>
      {config.icon}
      {config.label}
    </span>
  );
};
